/**
 * Collection Menu Navigation JavaScript
 * Save this as: assets/collection-menu-nav.js
 */

class ShopifyMenuNav {
  constructor(wrapperId, containerId, sectionId) {
    this.sectionId = sectionId;
    this.wrapper = document.getElementById(wrapperId);
    this.container = document.getElementById(containerId);
    this.backButton = document.getElementById(`backButton-${sectionId}`);
    this.backButtonText = document.getElementById(`backButtonText-${sectionId}`);
    this.menuButtons = document.getElementById(`menuButtons-${sectionId}`);
    this.scrollArrowLeft = document.getElementById(`scrollArrowLeft-${sectionId}`);
    this.scrollArrowRight = document.getElementById(`scrollArrowRight-${sectionId}`);
    this.menuData = null;
    this.currentCollectionId = null;
    this.isLoading = false;

    // Get settings from global variable
    this.settings = window.collectionNavSettings?.[sectionId] || {};

    // Arrow scrolling properties
    this.scrollAmount = this.settings.scrollAmount || 250;
    this.scrollInterval = null;

    this.loadMenuData();
  }

  /**
   * Load menu data from JSON asset
   */
  async loadMenuData() {
    this.isLoading = true;

    // Show enhanced skeleton loader
    this.showSkeletonLoader();

    try {
      const headerJsonScript = document.getElementById('header-json');
      if (!headerJsonScript) throw new Error('header-json script not found');

      this.menuData = JSON.parse(headerJsonScript.textContent);

      // Add a small delay to show loading state (remove in production if not needed)
      await new Promise((resolve) => setTimeout(resolve, 300));

      this.isLoading = false;
      this.init();
    } catch (error) {
      console.error('Error loading menu:', error);
      this.isLoading = false;
      this.showError();
    }
  }

  /**
   * Show skeleton loader during loading
   */
  showSkeletonLoader() {
    this.menuButtons.innerHTML = `
        <div class="plp-menu-nav__loading">
          <div class="plp-menu-nav__skeleton-button"></div>
          <div class="plp-menu-nav__skeleton-button"></div>
          <div class="plp-menu-nav__skeleton-button"></div>
          <div class="plp-menu-nav__skeleton-button"></div>
        </div>
      `;
  }

  /**
   * Show error state
   */
  showError() {
    this.menuButtons.innerHTML = '<div class="plp-menu-nav__error">Failed to load navigation menu</div>';
    this.wrapper.classList.add('loaded');
  }

  /**
   * Initialize the navigation
   */
  init() {
    this.initializeCurrentCollection();
    this.render();
    this.bindEvents();
    this.initializeArrowScrolling();
  }

  /**
   * Initialize current collection from URL parameter or by matching current URL
   */
  initializeCurrentCollection() {
    const urlParams = new URLSearchParams(window.location.search);
    const collectionId = urlParams.get('collection');

    if (collectionId) {
      this.currentCollectionId = collectionId;
    } else {
      // Use DFS to find collection by matching current URL
      const currentPath = window.location.pathname;
      const foundCollection = this.findCollectionByUrl(this.menuData, currentPath);
      if (foundCollection) {
        this.currentCollectionId = foundCollection.id;
        this.updateUrlParameter(foundCollection.id);
      } else {
        // Default to root if no match
        this.currentCollectionId = this.menuData.id;
      }
    }
  }

  /**
   * DFS to find collection by URL
   */
  findCollectionByUrl(node, targetUrl) {
    if (node.url === targetUrl) return node;

    for (const child of node.children || []) {
      const found = this.findCollectionByUrl(child, targetUrl);
      if (found) return found;
    }
    return null;
  }

  /**
   * Find collection by ID in the tree
   */
  findCollectionById(node, targetId) {
    if (node.id === targetId) return node;

    for (const child of node.children || []) {
      const found = this.findCollectionById(child, targetId);
      if (found) return found;
    }
    return null;
  }

  /**
   * Find parent of a collection
   */
  findParentCollection(node, targetId, parent = null) {
    if (node.id === targetId) return parent;

    for (const child of node.children || []) {
      const found = this.findParentCollection(child, targetId, node);
      if (found) return found;
    }
    return null;
  }

  /**
   * Update URL parameter
   */
  updateUrlParameter(collectionId) {
    const url = new URL(window.location);
    url.searchParams.set('collection', collectionId);
    window.history.pushState({}, '', url);
  }

  /**
   * Get menu items for current collection
   */
  getMenuItems() {
    if (!this.menuData || !this.currentCollectionId) return [];

    const currentCollection = this.findCollectionById(this.menuData, this.currentCollectionId);
    if (!currentCollection) return [];

    // If current collection has children, show them
    if (currentCollection.children && currentCollection.children.length > 0) {
      const items = this.sortMenuItems([currentCollection, ...currentCollection.children], currentCollection.id);
      return { items, allButtonId: currentCollection.id };
    }

    // If no children (leaf node), show parent's children
    const parent = this.findParentCollection(this.menuData, this.currentCollectionId);
    if (parent && parent.children) {
      const items = this.sortMenuItems([parent, ...parent.children], parent.id);
      return { items, allButtonId: parent.id };
    }

    // Fallback to root children
    const items = this.sortMenuItems([this.menuData, ...this.menuData.children], this.menuData.id);
    return { items, allButtonId: this.menuData.id };
  }

  /**
   * Sort menu items with "All" button first
   */
  sortMenuItems(items, allButtonId) {
    // Separate "All" button and other items
    const allButtonItems = items.filter((item) => item.id === allButtonId);
    const otherItems = items.filter((item) => item.id !== allButtonId);

    // Sort other items by sortOrder
    otherItems.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    // Return "All" button first, then sorted other items
    return [...allButtonItems, ...otherItems];
  }

  /**
   * Get back navigation info
   */
  getBackNavigationInfo() {
    if (!this.menuData || !this.currentCollectionId) return null;

    const currentCollection = this.findCollectionById(this.menuData, this.currentCollectionId);
    const parent = this.findParentCollection(this.menuData, this.currentCollectionId);

    if (!parent) return null;

    // If current collection has children, back button shows current collection's title
    if (currentCollection && currentCollection.children && currentCollection.children.length > 0) {
      return {
        collection: parent,
        title: currentCollection.title,
      };
    }

    // If current collection has no children (leaf), back button shows parent's title
    // but navigates to grandparent collection
    const grandparent = this.findParentCollection(this.menuData, parent.id);

    return {
      collection: grandparent || parent, // Use grandparent if it exists, otherwise fallback to parent
      title: parent.title,
    };
  }

  /**
   * Handle navigation click
   */
  handleNavigationClick(collection) {
    if (this.isLoading) return; // Prevent clicks during loading

    // Add loading state
    this.wrapper.classList.remove('loaded');

    this.currentCollectionId = collection.id;
    this.updateUrlParameter(collection.id);

    // Navigate to collection page
    window.location.href = collection.url + '?collection=' + collection.id;
  }

  /**
   * Initialize arrow scrolling functionality
   */
  initializeArrowScrolling() {
    // Only show arrows on desktop with mouse
    if (window.matchMedia('(pointer: fine)').matches && window.innerWidth > 768) {
      this.scrollArrowLeft.addEventListener('mousedown', this.handleArrowMouseDown.bind(this, 'left'));
      this.scrollArrowRight.addEventListener('mousedown', this.handleArrowMouseDown.bind(this, 'right'));
      this.scrollArrowLeft.addEventListener('mouseup', this.handleArrowMouseUp.bind(this));
      this.scrollArrowRight.addEventListener('mouseup', this.handleArrowMouseUp.bind(this));
      this.scrollArrowLeft.addEventListener('mouseleave', this.handleArrowMouseUp.bind(this));
      this.scrollArrowRight.addEventListener('mouseleave', this.handleArrowMouseUp.bind(this));
      this.scrollArrowLeft.addEventListener('click', (e) => this.scrollByDirection('left', e));
      this.scrollArrowRight.addEventListener('click', (e) => this.scrollByDirection('right', e));

      // Update arrow visibility on scroll
      this.container.addEventListener('scroll', this.throttle(this.updateArrowVisibility.bind(this), 16));

      // Initial arrow visibility check
      setTimeout(() => this.updateArrowVisibility(), 100);
    }
  }

  /**
   * Handle arrow mouse down (start continuous scrolling)
   */
  handleArrowMouseDown(direction) {
    // Start continuous scrolling on hold
    this.scrollInterval = setInterval(() => {
      this.scrollByDirection(direction);
    }, 100);
  }

  /**
   * Handle arrow mouse up (stop continuous scrolling)
   */
  handleArrowMouseUp() {
    // Stop continuous scrolling
    if (this.scrollInterval) {
      clearInterval(this.scrollInterval);
      this.scrollInterval = null;
    }
  }

  /**
   * Scroll container by direction
   */
  scrollByDirection(direction, event = null) {
    if (event) event.preventDefault();

    const scrollAmount = direction === 'left' ? -this.scrollAmount : this.scrollAmount;
    this.container.scrollBy({
      left: scrollAmount,
      behavior: 'smooth',
    });
  }

  /**
   * Update arrow visibility based on scroll position
   */
  updateArrowVisibility() {
    if (!this.scrollArrowLeft || !this.scrollArrowRight) return;

    const isOverflowing = this.container.scrollWidth > this.container.clientWidth;
    const isAtStart = this.container.scrollLeft <= 1;
    const isAtEnd = this.container.scrollLeft >= this.container.scrollWidth - this.container.clientWidth - 1;

    // Show/hide left arrow
    if (isOverflowing && !isAtStart) {
      this.scrollArrowLeft.classList.add('visible');
    } else {
      this.scrollArrowLeft.classList.remove('visible');
    }

    // Show/hide right arrow
    if (isOverflowing && !isAtEnd) {
      this.scrollArrowRight.classList.add('visible');
    } else {
      this.scrollArrowRight.classList.remove('visible');
    }
  }

  /**
   * Throttle function calls
   */
  throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  /**
   * Render the navigation menu
   */
  render() {
    if (this.isLoading) return;

    // Remove loaded class for smooth transition
    this.wrapper.classList.remove('loaded');

    const menuData = this.getMenuItems();
    const menuItems = menuData.items || [];
    const allButtonId = menuData.allButtonId;
    const backNavigationInfo = this.getBackNavigationInfo();

    // Handle back button with smooth transition
    if (backNavigationInfo) {
      this.backButtonText.textContent = backNavigationInfo.title;
      this.backButton.onclick = () => this.handleNavigationClick(backNavigationInfo.collection);
      this.backButton.style.display = 'flex';

      // Smooth back button appearance
      requestAnimationFrame(() => {
        this.backButton.classList.add('visible');
      });
    } else {
      this.backButton.classList.remove('visible');
      setTimeout(() => {
        this.backButton.style.display = 'none';
      }, this.settings.animationDuration || 300);
    }

    // Clear menu buttons
    this.menuButtons.innerHTML = '';

    // Add buttons with staggered animation
    menuItems.forEach((item, index) => {
      const isActive = item.id === this.currentCollectionId;
      const isAllButton = item.id === allButtonId;

      const buttonContainer = document.createElement('div');
      buttonContainer.className = 'plp-menu-nav__button-container';

      const button = document.createElement('button');
      button.className = `plp-menu-nav__button font-btn-s ${isActive ? 'plp-menu-nav__button--active' : ''}`;
      button.textContent = isAllButton ? 'All' : item.title;
      button.onclick = () => this.handleNavigationClick(item);

      // Add accessibility attributes
      button.setAttribute('aria-label', `Navigate to ${isAllButton ? 'All ' + item.title : item.title}`);
      if (isActive) {
        button.setAttribute('aria-current', 'page');
      }

      buttonContainer.appendChild(button);

      if (isActive) {
        const activeBar = document.createElement('div');
        activeBar.className = 'plp-menu-nav__active-bar';
        buttonContainer.appendChild(activeBar);
      }

      this.menuButtons.appendChild(buttonContainer);

      // Staggered animation
      setTimeout(() => {
        buttonContainer.classList.add('animate-in');
      }, index * (this.settings.staggerDelay || 50));
    });

    // Mark wrapper as loaded and update arrow visibility after buttons are rendered
    setTimeout(() => {
      this.wrapper.classList.add('loaded');
      this.updateArrowVisibility();
    }, menuItems.length * (this.settings.staggerDelay || 50) + 100);
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Handle browser back/forward buttons
    window.addEventListener('popstate', () => {
      this.initializeCurrentCollection();
      this.render();
    });

    // Handle keyboard navigation for accessibility
    this.container.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const buttons = this.container.querySelectorAll('.plp-menu-nav__button');
        const currentIndex = Array.from(buttons).indexOf(document.activeElement);

        if (currentIndex !== -1) {
          e.preventDefault();
          const nextIndex =
            e.key === 'ArrowRight' ? Math.min(currentIndex + 1, buttons.length - 1) : Math.max(currentIndex - 1, 0);
          buttons[nextIndex].focus();
        }
      }
    });

    // Handle window resize for arrow visibility
    window.addEventListener(
      'resize',
      this.throttle(() => {
        this.updateArrowVisibility();
      }, 250)
    );
  }
}

/**
 * Initialize navigation instances
 */
function initializeCollectionNavigation() {
  // Find all navigation wrappers and initialize them
  const wrappers = document.querySelectorAll('[id^="plpMenuNavWrapper-"]');

  wrappers.forEach((wrapper) => {
    const sectionId = wrapper.id.replace('plpMenuNavWrapper-', '');
    const wrapperId = `plpMenuNavWrapper-${sectionId}`;
    const containerId = `plpMenuNav-${sectionId}`;

    const container = document.getElementById(containerId);
    if (wrapper && container) {
      // Prevent layout shift
      container.style.minHeight = '60px';

      // Initialize navigation
      new ShopifyMenuNav(wrapperId, containerId, sectionId);
    }
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () {
    setTimeout(initializeCollectionNavigation, 100);
  });
} else {
  setTimeout(initializeCollectionNavigation, 100);
}
